import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy,
  writeBatch,
  serverTimestamp,
  increment,
  runTransaction
} from 'firebase/firestore'
import { db } from '@/config/firebase'
import { 
  Challenge, 
  CreateChallengeInput, 
  JoinChallengeResult,
} from '@/types/challenge'
import { UserChallengeRepository } from './UserChallengeRepository'
import { ChallengeRepository } from './ChallengeRepository'
import { v4 as uuidv4 } from 'uuid'

/**
 * Firebase implementation of UserChallengeRepository
 * Handles user challenge instances (not challenges)
 */
export class FirebaseUserChallengeRepository implements UserChallengeRepository {
  private userId?: string

  constructor(userId?: string) {
    this.userId = userId
  }

  async create(input: CreateChallengeInput): Promise<Challenge> {
    const challengeId = uuidv4()
    const now = new Date().toISOString()
    
    const challenge: Challenge = {
      id: challengeId,
      title: input.title,
      type: input.type,
      challengeId: input.challengeId,
      frequency: input.frequency,
      status: 'active',
      ownerUserId: this.userId,
      createdAt: now,
      updatedAt: now,
      currentStreak: 0,
      bestStreak: 0,
      totalCheckIns: 0,
      sharedChallengeId: challengeId // Original challenge uses its own ID as sharedChallengeId
    }

    if (this.userId) {
      // Store in Firestore for authenticated users
      const docRef = doc(db, 'userChallenges', challengeId)
      const { id, createdAt, updatedAt, ...challengeData } = challenge // Remove id and timestamp fields for Firestore
      
      // Filter out undefined values and problematic fields to prevent Firestore validation errors
      const cleanedData = Object.fromEntries(
        Object.entries(challengeData).filter(([key, value]) => {
          if (value === undefined || value === null) return false
          // Remove empty arrays as they can cause Firestore issues
          if (Array.isArray(value) && value.length === 0) return false
          // Ensure frequency object doesn't have undefined nested values
          if (key === 'frequency' && typeof value === 'object') {
            const cleanFreq = Object.fromEntries(
              Object.entries(value).filter(([_, v]) => v !== undefined && v !== null)
            )
            challengeData.frequency = cleanFreq
          }
          return true
        })
      )
      
      
      await setDoc(docRef, {
        ...cleanedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true })
    } else {
      // Store in localStorage for guest users
      this.saveToLocalStorage(challenge)
    }

    // If this challenge is based on a catalog challenge, increment the challenge's participant count
    if (input.challengeId && input.type === 'catalog') {
      try {
        const challengeRepo = new ChallengeRepository()
        await challengeRepo.incrementParticipantCount(input.challengeId)
      } catch (error) {
        // Expected error - users don't have permission to update challenge counts
        // This is handled server-side, so we can safely ignore
      }
    }

    return challenge
  }

  async update(id: string, patch: Partial<Challenge>): Promise<void> {
    if (this.userId) {
      const docRef = doc(db, 'userChallenges', id)
      
      // Filter out undefined values to prevent Firestore validation errors
      const cleanedPatch = Object.fromEntries(
        Object.entries(patch).filter(([_, value]) => value !== undefined)
      )
      
      
      await updateDoc(docRef, {
        ...cleanedPatch,
        updatedAt: serverTimestamp()
      })
    } else {
      const challenges = this.getFromLocalStorage()
      const index = challenges.findIndex(c => c.id === id)
      if (index !== -1) {
        challenges[index] = { ...challenges[index], ...patch, updatedAt: new Date().toISOString() }
        this.saveAllToLocalStorage(challenges)
      }
    }
  }

  async findById(id: string): Promise<Challenge | null> {
    if (this.userId) {
      const docRef = doc(db, 'userChallenges', id)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Challenge
      }
      return null
    } else {
      const challenges = this.getFromLocalStorage()
      return challenges.find(c => c.id === id) || null
    }
  }

  async listByOwner(userId?: string): Promise<Challenge[]> {
    const targetUserId = userId || this.userId
    
    if (targetUserId) {
      const q = query(
        collection(db, 'userChallenges'),
        where('ownerUserId', '==', targetUserId)
      )
      const snapshot = await getDocs(q)
      // Sort in memory instead of using orderBy to avoid composite index requirement
      const challenges = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Challenge))
      return challenges.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime()
        const dateB = new Date(b.createdAt).getTime()
        return dateB - dateA // desc order
      })
    } else {
      // Guest mode - get from localStorage
      return this.getFromLocalStorage()
    }
  }

  async delete(id: string): Promise<void> {
    if (this.userId) {
      const docRef = doc(db, 'userChallenges', id)
      await deleteDoc(docRef)
    } else {
      const challenges = this.getFromLocalStorage()
      const filtered = challenges.filter(c => c.id !== id)
      this.saveAllToLocalStorage(filtered)
    }
  }

  async pause(id: string): Promise<void> {
    await this.update(id, {
      status: 'paused',
      pausedAt: new Date().toISOString()
    })
  }

  async pauseWithDuration(id: string, pauseUntil?: string, reason?: string): Promise<void> {
    await this.update(id, {
      status: 'paused',
      pausedAt: new Date().toISOString(),
      pauseUntil,
      pauseReason: reason
    })
  }

  async resume(id: string): Promise<void> {
    await this.update(id, {
      status: 'active',
      pausedAt: undefined,
      pauseUntil: undefined,
      pauseReason: undefined
    })
  }

  async validateCheckIn(id: string, checkInDate?: string): Promise<Challenge> {
    const challenge = await this.findById(id)
    if (!challenge) {
      throw new Error('Challenge not found')
    }

    const today = checkInDate || new Date().toISOString().split('T')[0]
    const existingValidatedDays = challenge.validatedDays || []
    
    // Ensure consistent date format (YYYY-MM-DD)
    const normalizedToday = today.includes('T') ? today.split('T')[0] : today
    
    // Check if this day has already been validated
    if (existingValidatedDays.includes(normalizedToday)) {
      throw new Error('This day has already been validated')
    }
    
    const validatedDays = [...existingValidatedDays, normalizedToday]
    const totalCheckIns = challenge.totalCheckIns + 1
    
    // Calculate streak
    let currentStreak = 1
    const sortedDays = validatedDays.sort()
    for (let i = sortedDays.length - 2; i >= 0; i--) {
      const prevDate = new Date(sortedDays[i])
      const currDate = new Date(sortedDays[i + 1])
      const diffTime = Math.abs(currDate.getTime() - prevDate.getTime())
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 1) {
        currentStreak++
      } else {
        break
      }
    }

    const bestStreak = Math.max(challenge.bestStreak, currentStreak)

    await this.update(id, {
      validatedDays,
      totalCheckIns,
      currentStreak,
      bestStreak,
      lastCheckIn: new Date().toISOString()
    })

    return this.findById(id) as Promise<Challenge>
  }

  async generateShareToken(challengeId: string): Promise<string> {
    const token = uuidv4()
    
    if (this.userId) {
      const shareDoc = {
        id: uuidv4(),
        challengeId,
        token,
        createdAt: serverTimestamp(),
        isActive: true,
        usageCount: 0
      }
      
      await addDoc(collection(db, 'challengeShares'), shareDoc)
    } else {
      // Store in localStorage for guest
      const shares = this.getSharesFromLocalStorage()
      shares.push({
        id: uuidv4(),
        challengeId,
        token,
        createdAt: new Date().toISOString(),
        isActive: true,
        usageCount: 0
      })
      this.saveSharesToLocalStorage(shares)
    }
    
    return token
  }

  async findByShareToken(token: string): Promise<Challenge | null> {
    if (this.userId) {
      const q = query(
        collection(db, 'challengeShares'),
        where('token', '==', token),
        where('isActive', '==', true)
      )
      const snapshot = await getDocs(q)
      
      if (snapshot.empty) {
        return null
      }
      
      const shareDoc = snapshot.docs[0]
      const share = shareDoc.data()
      
      return this.findById(share.challengeId)
    } else {
      const shares = this.getSharesFromLocalStorage()
      const share = shares.find(s => s.token === token && s.isActive)
      
      if (share) {
        return this.findById(share.challengeId)
      }
      
      return null
    }
  }

  async joinChallenge(token: string, _userId?: string): Promise<JoinChallengeResult> {
    const challenge = await this.findByShareToken(token)
    
    if (!challenge) {
      return { success: false, error: 'Challenge not found or token invalid' }
    }

    // Check if user has already joined this challenge
    try {
      const userChallenges = await this.listByOwner()
      const sharedChallengeId = challenge.sharedChallengeId || challenge.id
      
      const hasAlreadyJoined = userChallenges.some(userChallenge => 
        userChallenge.challengeId === challenge.challengeId || 
        userChallenge.sharedChallengeId === sharedChallengeId
      )
      
      if (hasAlreadyJoined) {
        return { success: false, error: 'You have already joined this challenge' }
      }
    } catch (error) {
      // Continue with creation even if check fails
      // This might happen if user has no challenges yet
    }

    // Create a copy of the challenge for the new user
    const createInput: CreateChallengeInput = {
      title: challenge.title,
      type: challenge.type,
      frequency: challenge.frequency
    }
    
    // Only include challengeId if it exists
    if (challenge.challengeId) {
      createInput.challengeId = challenge.challengeId
    }
    
    const newChallenge = await this.create(createInput)

    // Update the new challenge with the shared challenge ID
    try {
      await this.update(newChallenge.id, { 
        sharedChallengeId: sharedChallengeId
      })
      newChallenge.sharedChallengeId = sharedChallengeId
    } catch (error) {
      // Continue even if update fails - challenge is already created
      // This is not critical for the challenge to work
    }

    // Schedule notifications for the joined challenge
    try {
      const { scheduleNotificationsForChallenge } = await import('@/utils/simpleChallengeUtils')
      await scheduleNotificationsForChallenge(newChallenge)
      if (import.meta.env.DEV) {
        console.log('Notifications scheduled for joined challenge:', newChallenge.title)
      }
    } catch (error) {
      console.error('Failed to schedule notifications for joined challenge:', error)
      // Don't fail the join operation if notification scheduling fails
    }

    return { success: true, challenge: newChallenge }
  }


  async revokeShareToken(challengeId: string): Promise<void> {
    if (this.userId) {
      const q = query(
        collection(db, 'challengeShares'),
        where('challengeId', '==', challengeId)
      )
      const snapshot = await getDocs(q)
      
      const batch = writeBatch(db)
      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, { isActive: false })
      })
      
      await batch.commit()
    } else {
      const shares = this.getSharesFromLocalStorage()
      const updated = shares.map(s => 
        s.challengeId === challengeId ? { ...s, isActive: false } : s
      )
      this.saveSharesToLocalStorage(updated)
    }
  }

  // Private methods for localStorage management
  private getFromLocalStorage(): Challenge[] {
    try {
      const data = localStorage.getItem('staychecked_user_challenges')
      const challenges = data ? JSON.parse(data) : []
      // Sort by createdAt in descending order (newest first) to match Firestore behavior
      return challenges.sort((a: Challenge, b: Challenge) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
    } catch {
      return []
    }
  }

  private saveToLocalStorage(challenge: Challenge): void {
    const challenges = this.getFromLocalStorage()
    challenges.push(challenge)
    this.saveAllToLocalStorage(challenges)
  }

  private saveAllToLocalStorage(challenges: Challenge[]): void {
    localStorage.setItem('staychecked_user_challenges', JSON.stringify(challenges))
  }

  private getSharesFromLocalStorage(): any[] {
    try {
      const data = localStorage.getItem('staychecked_challenge_shares')
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  }

  private saveSharesToLocalStorage(shares: any[]): void {
    localStorage.setItem('staychecked_challenge_shares', JSON.stringify(shares))
  }
}